// erp.ts - 기존 ERP 이관 화면(150개)의 클라이언트 계약 + 순수 로직.
//
// 화면은 서버 명세(`GET /api/erp/meta`)로 그려진다 - 뷰마다 컴포넌트를 만들지
// 않는다. 그래서 이 파일이 소유하는 것은 (1) 와이어 매퍼, (2) 셀 표시 문자열,
// (3) 폼 초안 <-> 저장 페이로드 변환, (4) CSV 내보내기, (5) 화면 검색이다.
// 전부 DOM 없이 `erp.test.ts` 로 검증한다.
// 네트워크 호출은 `api.ts` 의 ERP 섹션에 있다 (이 파일은 런타임 의존이 없어야 DOM/Firebase 없이 테스트된다).
import { EMPTY, fmt, fmtDec, pct } from "@/lib/format";
import { searchNorm } from "@/lib/search";

export type ErpFieldType =
  | "text"
  | "longtext"
  | "int"
  | "number"
  | "money"
  | "pct"
  | "date"
  | "month"
  | "time"
  | "bool"
  | "enum"
  | "ref"
  | "file";

export interface ErpField {
  key: string;
  label: string;
  type: ErpFieldType;
  ref: string | null;
  options: string[];
  required: boolean;
  unit: string;
  /** 다른 필드로 계산되는 값 (금액 = 수량 × 단가) - 입력받지 않는다 */
  readonly: boolean;
  /** 비우면 서버가 채번 */
  auto: boolean;
  help: string;
  /** 새 레코드 기본값 (없으면 null) */
  default: string | number | boolean | null;
}

export interface ErpEntity {
  key: string;
  title: string;
  module: string;
  fields: ErpField[];
  dateField: string | null;
  adminWrite: boolean;
}

export interface ErpColumn {
  key: string;
  label: string;
  type: ErpFieldType;
  total: boolean;
  unit: string;
  width: number | null;
}

export type ErpFilterType = "date_range" | "month" | "year" | "ref" | "enum" | "text" | "bool";

export interface ErpFilter {
  key: string;
  label: string;
  type: ErpFilterType;
  ref: string | null;
  options: string[];
}

export type ErpAction = "create" | "update" | "delete" | "export" | "print";

export interface ErpView {
  key: string;
  title: string;
  kind: "list" | "report" | "link";
  legacy: string;
  entity: string | null;
  /** kind="link" 일 때 이동할 PDS 화면 id */
  link: string;
  actions: ErpAction[];
  columns: ErpColumn[];
  filters: ErpFilter[];
  defaults: Record<string, string>;
  notes: string[];
  pageSize: number;
}

export interface ErpModule {
  key: string;
  title: string;
  views: ErpView[];
}

export interface ErpMeta {
  asOf: string;
  modules: ErpModule[];
  entities: Record<string, ErpEntity>;
}

export type ErpRow = Record<string, unknown>;

export interface ErpViewResult {
  columns: ErpColumn[];
  rows: ErpRow[];
  total: number;
  totals: ErpRow | null;
  notes: string[];
  params: Record<string, string>;
}

export interface ErpOption {
  id: string;
  label: string;
}

export interface ErpFile {
  key: string;
  filename: string;
  size: number;
  uploadedAt: string;
}

/* =====================================================================
   와이어 매퍼 (snake_case -> camelCase, 필드마다 손으로)
   ===================================================================== */

export type Wire = Record<string, unknown>;
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const arr = <T>(v: unknown, f: (x: Wire) => T): T[] =>
  Array.isArray(v) ? v.map((x) => f(x as Wire)) : [];

const toColumn = (c: Wire): ErpColumn => ({
  key: str(c.key),
  label: str(c.label),
  type: (str(c.type) || "text") as ErpFieldType,
  total: c.total === true,
  unit: str(c.unit),
  width: typeof c.width === "number" ? c.width : null,
});

const toField = (f: Wire): ErpField => ({
  key: str(f.key),
  label: str(f.label),
  type: (str(f.type) || "text") as ErpFieldType,
  ref: typeof f.ref === "string" ? f.ref : null,
  options: Array.isArray(f.options) ? (f.options as string[]) : [],
  required: f.required === true,
  unit: str(f.unit),
  readonly: f.readonly === true,
  auto: f.auto === true,
  help: str(f.help),
  default:
    typeof f.default === "string" || typeof f.default === "number" || typeof f.default === "boolean"
      ? f.default
      : null,
});

const toView = (v: Wire): ErpView => ({
  key: str(v.key),
  title: str(v.title),
  kind: (str(v.kind) || "list") as ErpView["kind"],
  legacy: str(v.legacy),
  entity: typeof v.entity === "string" ? v.entity : null,
  link: str(v.link),
  actions: Array.isArray(v.actions) ? (v.actions as ErpAction[]) : [],
  columns: arr(v.columns, toColumn),
  filters: arr(v.filters, (f) => ({
    key: str(f.key),
    label: str(f.label),
    type: str(f.type) as ErpFilterType,
    ref: typeof f.ref === "string" ? f.ref : null,
    options: Array.isArray(f.options) ? (f.options as string[]) : [],
  })),
  defaults: (v.defaults ?? {}) as Record<string, string>,
  notes: Array.isArray(v.notes) ? (v.notes as string[]) : [],
  pageSize: typeof v.page_size === "number" ? v.page_size : 100,
});

export const toMeta = (w: Wire): ErpMeta => {
  const entities: Record<string, ErpEntity> = {};
  for (const [k, e] of Object.entries((w.entities ?? {}) as Record<string, Wire>)) {
    entities[k] = {
      key: str(e.key),
      title: str(e.title),
      module: str(e.module),
      fields: arr(e.fields, toField),
      dateField: typeof e.date_field === "string" ? e.date_field : null,
      adminWrite: e.admin_write === true,
    };
  }
  return {
    asOf: str(w.as_of),
    modules: arr(w.modules, (m) => ({
      key: str(m.key),
      title: str(m.title),
      views: arr(m.views, toView),
    })),
    entities,
  };
};

export const toResult = (w: Wire): ErpViewResult => ({
  columns: arr(w.columns, toColumn),
  rows: Array.isArray(w.rows) ? (w.rows as ErpRow[]) : [],
  total: typeof w.total === "number" ? w.total : 0,
  totals: (w.totals ?? null) as ErpRow | null,
  notes: Array.isArray(w.notes) ? (w.notes as string[]) : [],
  params: (w.params ?? {}) as Record<string, string>,
});

/** 검색조건 쿼리. 빈 값은 "조건 없음"으로 **명시적으로** 보낸다 - 서버 기본값(예: 재직자만)을 끄려면 필요하다. */
export function viewQuery(params: Record<string, string>, offset = 0, limit = 0): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) sp.set(k, v);
  if (offset) sp.set("offset", String(offset));
  if (limit) sp.set("limit", String(limit));
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/* =====================================================================
   표시 (순수)
   ===================================================================== */

const NUMERIC: ReadonlySet<ErpFieldType> = new Set(["int", "number", "money", "pct"]);
export const isNumeric = (t: ErpFieldType): boolean => NUMERIC.has(t);

const asNumber = (v: unknown): number | null => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number.parseFloat(v.replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/** 표 셀 1칸의 보이는 문자열. 참조 열은 서버가 붙인 `{key}__label` 을 쓴다. */
export function cellText(col: Pick<ErpColumn, "key" | "type">, row: ErpRow): string {
  const v = row[col.key];
  switch (col.type) {
    case "ref": {
      const label = row[`${col.key}__label`];
      return typeof label === "string" && label ? label : v ? String(v) : EMPTY;
    }
    case "int":
    case "money": {
      const n = asNumber(v);
      return n === null ? EMPTY : fmt(n);
    }
    case "number": {
      const n = asNumber(v);
      return n === null ? EMPTY : fmtDec(n, Number.isInteger(n) ? 0 : 2);
    }
    case "pct":
      return pct(asNumber(v));
    case "bool":
      return v === true ? "Y" : "";
    case "file":
      return Array.isArray(v) && v.length ? `첨부 ${v.length}` : "";
    default:
      return v === null || v === undefined || v === "" ? "" : String(v);
  }
}

/** CSV 1칸 - 숫자는 쉼표 없는 원값 (엑셀이 숫자로 읽게). */
function csvValue(col: ErpColumn, row: ErpRow): string {
  if (isNumeric(col.type)) {
    const n = asNumber(row[col.key]);
    return n === null ? "" : String(n);
  }
  return cellText(col, row);
}

const csvEscape = (s: string): string => (/[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);

/** 엑셀출력 대체. UTF-8 BOM 을 붙여야 엑셀이 한글을 깨지 않는다. */
export function toCsv(columns: ErpColumn[], rows: ErpRow[], totals?: ErpRow | null): string {
  const lines = [columns.map((c) => csvEscape(c.label)).join(",")];
  for (const r of rows) lines.push(columns.map((c) => csvEscape(csvValue(c, r))).join(","));
  if (totals && Object.keys(totals).length) {
    lines.push(
      columns
        .map((c, i) => (c.key in totals ? csvEscape(csvValue(c, totals)) : i === 0 ? "합계" : ""))
        .join(",")
    );
  }
  return `﻿${lines.join("\r\n")}`;
}

/* =====================================================================
   검색조건 (순수)
   ===================================================================== */

/** 검색조건 1개가 쓰는 쿼리 키. 기간은 `_from`/`_to` 두 개. */
export const filterKeys = (f: Pick<ErpFilter, "key" | "type">): string[] =>
  f.type === "date_range" ? [`${f.key}_from`, `${f.key}_to`] : [f.key];

/** 화면 진입 시 검색조건 - 서버 기본값 + 모든 키를 빈 문자열로라도 채운다 (제어 컴포넌트). */
export function initialParams(view: Pick<ErpView, "filters" | "defaults">): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of view.filters) for (const k of filterKeys(f)) out[k] = view.defaults[k] ?? "";
  return out;
}

/* =====================================================================
   폼 (순수)
   ===================================================================== */

export type Draft = Record<string, string | boolean>;

/** 입력 가능한 필드 (파생·첨부 제외). */
export const editableFields = (e: ErpEntity): ErpField[] =>
  e.fields.filter((f) => !f.readonly && f.type !== "file");

export function draftOf(e: ErpEntity, row?: ErpRow | null): Draft {
  const d: Draft = {};
  for (const f of editableFields(e)) {
    const v = row?.[f.key];
    if (f.type === "bool") d[f.key] = row ? v === true : f.default === true;
    else if (row) d[f.key] = v === null || v === undefined ? "" : String(v);
    else d[f.key] = f.default === null ? "" : String(f.default);
  }
  return d;
}

const parseNum = (s: string): number | null => {
  const t = s.replace(/,/g, "").trim();
  if (t === "") return null;
  const n = Number.parseFloat(t);
  return Number.isFinite(n) ? n : Number.NaN;
};

/**
 * 초안 -> 저장 페이로드. `original` 이 있으면 바뀐 필드만 (PATCH).
 * 숫자 변환 실패는 문자열 그대로 보내 서버 422 메시지를 받는다 - 검증 규칙은 서버 한 곳.
 */
export function payloadOf(e: ErpEntity, draft: Draft, original?: Draft | null): ErpRow {
  const out: ErpRow = {};
  for (const f of editableFields(e)) {
    const v = draft[f.key];
    if (original && original[f.key] === v) continue;
    if (f.type === "bool") {
      out[f.key] = v === true;
    } else if (isNumeric(f.type)) {
      const n = parseNum(String(v ?? ""));
      out[f.key] = n === null ? "" : Number.isNaN(n) ? String(v) : n;
    } else {
      out[f.key] = String(v ?? "");
    }
  }
  return out;
}

/** 클라이언트 측 필수값 확인 (서버도 다시 본다). */
export function missingRequired(e: ErpEntity, draft: Draft): string[] {
  return editableFields(e)
    .filter((f) => f.required && !f.auto && (draft[f.key] === "" || draft[f.key] === undefined))
    .map((f) => f.key);
}

/** 422 응답의 필드별 오류. 다른 오류면 빈 객체. (`ApiError` 형태로 판별 - 런타임 import 없음) */
export function fieldErrors(err: unknown): Record<string, string> {
  const e = err as { status?: unknown; detail?: unknown } | null;
  if (!e || typeof e !== "object" || e.status !== 422) return {};
  const d = e.detail as { errors?: unknown } | undefined;
  if (!d || typeof d.errors !== "object" || d.errors === null) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(d.errors as Record<string, unknown>)) {
    if (typeof v === "string") out[k] = v;
  }
  return out;
}

/* =====================================================================
   화면 찾기 (순수) - 150개 화면을 메뉴만으로 찾기 어렵다
   ===================================================================== */

export interface ViewHit {
  module: ErpModule;
  view: ErpView;
}

export function searchViews(modules: ErpModule[], q: string): ViewHit[] {
  // 토큰마다 포함 (순서 무관, 띄어쓰기 무시): "금형 현황" -> "금형(TOOL)현황(수명관리)"
  const tokens = searchNorm(q).split(" ").filter(Boolean);
  if (!tokens.length) return [];
  const hits: ViewHit[] = [];
  for (const m of modules) {
    for (const v of m.views) {
      const hay = searchNorm(`${m.title}${v.title}`).replace(/\s/g, "");
      if (tokens.every((t) => hay.includes(t))) hits.push({ module: m, view: v });
    }
  }
  return hits;
}

/**
 * 열 ERP 메뉴: 주소(`/erp/<모듈>/<메뉴>`) → 마지막으로 본 메뉴 → 첫 모듈의 첫 메뉴.
 * `missing` = 주소가 가리킨 메뉴가 없다 (화면이 알리고 주소를 바로잡는다).
 */
export function resolveView(
  modules: ErpModule[],
  wanted: string | undefined,
  saved: string | null
): { module: ErpModule; view: ErpView; missing: boolean } | null {
  const find = (key: string | null | undefined) => {
    if (!key) return null;
    for (const m of modules) {
      const v = m.views.find((x) => x.key === key);
      if (v) return { module: m, view: v };
    }
    return null;
  };
  const hit = find(wanted);
  if (hit) return { ...hit, missing: false };
  const missing = Boolean(wanted);
  const fallback = find(saved);
  if (fallback) return { ...fallback, missing };
  const m = modules.find((x) => x.views.length > 0);
  const first = m?.views[0];
  return m && first ? { module: m, view: first, missing } : null;
}

/** 쓰기 가능 여부 - 마스터성 엔티티(`adminWrite`)는 관리자만. 서버도 같은 규칙으로 403 을 준다. */
export const canWrite = (entity: ErpEntity | null, isAdmin: boolean): boolean =>
  entity !== null && (!entity.adminWrite || isAdmin);
